import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import _ from 'lodash';

import HoldPositionPicker from './../../EventForm/components/HoldPositionPicker';
import RoomSelector from '../../../shared/RoomSelector';
import EventTemplateSelect from '../../../shared/EventTemplateSelect';
import { toastSuccess, toastError } from "../../../shared/toastHelper";

import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';
import {
  Announcement01, Building07, Button, Copy01, LayoutGrid01, MagicWand02, MarkerPin02, Passport, Plus, Tag, Tag01, UserSquare, copyText
} from '@opendate-app/setlist';
import { EventHeaderField } from '../../EventPage/components/EventHeader';
import { Calendar as CalendarIcon } from '@untitled-ui/icons-react';
import buildHoldDatesText from '../../OfferEditUI/utils/buildHoldDatesText';
import Popup from "reactjs-popup";
var moment = require('moment');

const axios = require('axios').default;

import {
  offsetDateForBrowser,
  offsetDateForServer
} from '../../../shared/timeZoneLogic';
import useVenueAgeRestrictionOptions from '../../shared/hooks/useVenueAgeRestrictionOptions';

const modalStyle = {
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "528px",
  "maxWidth": "calc(100vw - 16px)",
  "padding": "0px"
}

const holdModalStyle = { ...modalStyle, "width": "720px" }

const selectStyles = {
  container: (base, state) => ({
    ...base,
    opacity: state.isDisabled ? ".5" : "1",
    backgroundColor: "transparent",
    zIndex: "999"
  }),
  // Colors are token-driven so the selects theme with the page (light values match
  // the previous hardcoded colors; dark values resolve under body.od-themed).
  control: (base, state) => ({
    ...base,
    backgroundColor: 'var(--od-surface, #FFFFFF)',
    borderColor: state.isFocused ? 'var(--od-accent, var(--sl-border-brand))' : 'var(--od-border, var(--sl-border-primary))',
    borderRadius: '8px',
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
    '&:hover': {
      borderColor: 'var(--od-accent, var(--sl-border-brand))'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--od-accent, var(--sl-text-brand))',
    fontWeight: '600',
    fontSize: '14px'
  }),
  input: (base) => ({ ...base, color: 'var(--od-text, #181d27)' }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--od-text-2, var(--sl-text-tertiary))',
    fontSize: '14px'
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: provided => ({ ...provided, backgroundColor: 'var(--od-surface, #FFFFFF)', zIndex: "9999 !important" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--od-accent-subtle, #EFF6FB)'
      : (state.isFocused ? 'var(--od-surface-2, #F5F5F5)' : 'var(--od-surface, #FFFFFF)'),
    color: 'var(--od-text, #181d27)',
    '&:active': { backgroundColor: 'var(--od-surface-2, #F5F5F5)' }
  })
};

const formatDate = (time, venueTimeZone) => {
  if (typeof window === 'undefined') {
    return moment(new Date(time)).format("MM/DD/YY");
  }

  const datetime = offsetDateForBrowser(new Date(time), venueTimeZone);
  return moment(datetime).format("MM/DD/YY");
}

const formatTime = (time, venueTimeZone) => {
  if (typeof window === 'undefined') {
    return new Date(time).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric"
    });
  }

  const datetime = offsetDateForBrowser(new Date(time), venueTimeZone);
  return datetime.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric"
  });
}

const getHoldPosition = (hold, firstAvailable, defaultTopHold) => {
  const isExistingHold = !!hold.hold_position_id;
  return isExistingHold
    ? hold.position
    : (firstAvailable ? firstAvailable.position : (hold.position || defaultTopHold || 1));
};

const fetchCanonicalTags = async (team) => {
  try {
    const response = await axios.get(`/teams/${team.id}/canonical_tags`);
    return response.data.canonical_tags || [];
  } catch (error) {
    console.error('Error fetching canonical tags:', error);
    toastError("Could not fetch canonical tags");
    return [];
  }
};

const fetchTaggedItems = async (csrfToken, team, taggable) => {
  try {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const response = await axios.get(`/teams/${team.id}/tagged_items`, {
      params: {
        taggable_type: 'CalendarEvent',
        taggable_id: taggable.id
      }
    });
    return response.data || [];
  } catch (error) {
    console.error('Error fetching tagged items:', error);
    toastError("Could not fetch tags");
    return [];
  }
};

const fetchAvailableTags = async (csrfToken, team) => {
  try {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const response = await axios.get(`/teams/${team.id}/tags`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching available tags:', error);
    toastError("Could not fetch available tags");
    return [];
  }
};

const assignTag = async (csrfToken, team, taggable, tagId) => {
  try {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const response = await axios.post(`/teams/${team.id}/tagged_items`, {
      tagged_item: {
        taggable_type: 'CalendarEvent',
        taggable_id: taggable.id,
        tag_id: tagId
      }
    });
    return response.data;
  } catch (error) {
    const serverMessage = error.response?.data?.join?.(', ') || '';
    const message = serverMessage.toLowerCase().includes('already been taken')
      ? 'This tag is already assigned to this event'
      : (serverMessage || 'Failed to assign tag');
    console.error('Error assigning tag:', serverMessage);
    toastError(message);
    return null;
  }
};

const unassignTag = async (csrfToken, team, taggedItemId) => {
  try {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    await axios.delete(`/teams/${team.id}/tagged_items/${taggedItemId}`);
    return true;
  } catch (error) {
    console.error('Error removing tag:', error);
    toastError('Failed to remove tag');
    return false;
  }
};

const createAndAssignTag = async (csrfToken, team, confirm, tagName) => {
  try {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const response = await axios.post(`/teams/${team.id}/tagged_items`, {
      tagged_item: {
        taggable_id: confirm.id,
        taggable_type: 'CalendarEvent',
        tag_attributes: {
          name: tagName
        }
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating and assigning tag:', error);
    toastError('Could not create tag');
    return null;
  }
};

const handleCanonicalTagChange = (
  tagType,
  selectedValue,
  setTagFunction,
  otherSelectedTags,
  canonicalTags,
  eventDetailsChanged
) => {
  if (selectedValue) {
    setTagFunction([selectedValue]);
    const newCanonicalTagIds = [...otherSelectedTags, selectedValue];
    const newCanonicalTags = canonicalTags.filter(tag => newCanonicalTagIds.includes(tag.id));
    eventDetailsChanged(prevEventDetails => ({
      ...prevEventDetails,
      canonical_tags: newCanonicalTags
    }));
  } else {
    setTagFunction([]);
    const newCanonicalTags = canonicalTags.filter(tag => otherSelectedTags.includes(tag.id));
    eventDetailsChanged(prevEventDetails => ({
      ...prevEventDetails,
      canonical_tags: newCanonicalTags
    }));
  }
};

const EventDetails = ({
  csrfToken,
  team,
  confirm,
  currentUser,
  rooms = [],
  venueOwnerships = [],
  onConfirmSaved,
  onFinanceItemsChanged,
  buyers: initialBuyers = [],
  promoters: initialPromoters = [],
  isGuest = false,
  openOnMount = false,
  editRequest = 0,
  onClose = null,
  variant = "default"
}) => {
  const [buyers, setBuyers] = useState(initialBuyers);
  const [promoters, setPromoters] = useState(initialPromoters);
  const [eventDetails, setEventDetails] = useState({});
  const [holdGroupDates, setHoldGroupDates] = useState([]);
  const [makeUnavailable, setMakeUnavailable] = useState(false);
  const [canonicalTags, setCanonicalTags] = useState([]);
  const [selectedGenreTags, setSelectedGenreTags] = useState([]);
  const [selectedEventTypeTags, setSelectedEventTypeTags] = useState([]);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [taggedItems, setTaggedItems] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagSearchValue, setTagSearchValue] = useState('');
  const fetchedAvailabilityDates = useRef(new Set());

  const [selectedRooms, setSelectedRooms] = useState(
    rooms.filter((r) =>
      (confirm.room_ids || []).includes(r.id)
    ).map((r) => {
      return {
        value: r.id,
        label: r.name,
        room: r,
        venueOwnershipId: r.venue_ownership_id
      };
    })
  );

  const ageRestrictionVenueOwnershipId = selectedRooms[0]?.venueOwnershipId;
  const { options: ageRestrictionOptions } =
    useVenueAgeRestrictionOptions(team?.id, ageRestrictionVenueOwnershipId);

  const fetchBuyers = useCallback(async (inputValue) => {
    try {
      const response = await axios.get(`/teams/${team.id}/companies/autocomplete`, {
        params: { name: inputValue }
      });
      let newOptions = response.data;

      if (!newOptions.map(option => option.label).includes(inputValue)) {
        newOptions.unshift({
          value: inputValue,
          label: `Add "${inputValue}"`,
          name: inputValue
        });
      }

      return newOptions;
    } catch (error) {
      console.error('Error fetching buyers:', error);
      toastError("Could not fetch buyers");
      return [];
    }
  }, [team.id]);

  const createBuyer = useCallback(async (buyerName) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.post(`/teams/${team.id}/companies`, {
        company: { name: buyerName }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating buyer:', error);
      toastError("Could not create buyer");
      throw error;
    }
  }, [csrfToken, team.id]);

  const deleteBuyer = useCallback(async (buyerId) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/companies/${buyerId}`);
      return true;
    } catch (error) {
      toastError("Could not delete buyer");
      throw error;
    }
  }, [csrfToken, team.id]);

  const fetchPromoters = useCallback(async (inputValue) => {
    try {
      const response = await axios.get(`/teams/${team.id}/promoters/autocomplete`, {
        params: { name: inputValue }
      });
      let newOptions = response.data;

      if (!newOptions.map(option => option.label).includes(inputValue)) {
        newOptions.unshift({
          value: inputValue,
          label: `Add "${inputValue}"`,
          name: inputValue
        });
      }

      return newOptions;
    } catch (error) {
      console.error('Error fetching promoters:', error);
      toastError("Could not fetch promoters");
      return [];
    }
  }, [team.id]);

  const createPromoter = useCallback(async (promoterName) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.post(`/teams/${team.id}/promoters`, {
        promoter: { name: promoterName }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating promoter:', error);
      toastError("Could not create promoter");
      throw error;
    }
  }, [csrfToken, team.id]);

  const deletePromoter = useCallback(async (promoterId) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/promoters/${promoterId}`);
      return true;
    } catch (error) {
      console.error('Error deleting promoter:', error);
      toastError("Could not delete promoter");
      throw error;
    }
  }, [csrfToken, team.id]);

  const updateBuyer = useCallback(async (buyerId, newName) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.patch(`/teams/${team.id}/companies/${buyerId}`, {
        company: { name: newName }
      });
      return response.data;
    } catch (error) {
      toastError("Could not update buyer");
      throw error;
    }
  }, [csrfToken, team.id]);

  const updatePromoter = useCallback(async (promoterId, newName) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const response = await axios.patch(`/teams/${team.id}/promoters/${promoterId}`, {
        promoter: { name: newName }
      });
      return response.data;
    } catch (error) {
      toastError("Could not update promoter");
      throw error;
    }
  }, [csrfToken, team.id]);

  const fetchEventDetails = useCallback(async () => {
    // No CalendarEvent to fetch (e.g. a draft offer not yet linked to a hold or
    // confirm — its `confirm` is an offer-derived fallback with no id). Bail out
    // rather than GET /calendar_events//edit, which 404s and toasts an error.
    if (!confirm?.id) return;

    const promoterId = confirm.promoter?.id || confirm.promoter?.value || confirm.promoter;
    const buyerId = confirm.buyer?.id || confirm.buyer?.value || confirm.buyer;

    const baseEventDetails = {
      title: confirm.title || confirm.name,
      buyer: buyerId,
      promoter: promoterId,
      canonical_tags: confirm.canonical_tags || [],
      is_rental: confirm.is_rental || false,
      ticket_forecast: confirm.ticket_forecast || 0,
      age_restriction: confirm.age_restriction
    };

    try {
      const response = await axios.get(`/teams/${team.id}/calendar_events/${confirm.id}/edit`);
      const apiData = {
        ...response.data,
        promoter: response.data.promoter ? response.data.promoter.value : baseEventDetails.promoter,
        buyer: response.data.buyer ? response.data.buyer.value : baseEventDetails.buyer,
        canonical_tags: response.data.canonical_tags || baseEventDetails.canonical_tags
      };

      if (apiData.holdGroup) {
        apiData.holdGroup.hold_group_entries.forEach((holdGroupEntry) => {
          holdGroupEntry.hold.start_time = new Date(holdGroupEntry.hold.start_time);
        });
      }

      const finalEventDetails = {
        ...baseEventDetails,
        ...apiData
      };

      setEventDetails(finalEventDetails);
    } catch (error) {
      console.error('Error fetching event details:', error);
      toastError("Could not fetch event details");
      setEventDetails(baseEventDetails);
    }
  }, [team.id, confirm.id, confirm.title, confirm.name, JSON.stringify(confirm.buyer), JSON.stringify(confirm.promoter), JSON.stringify(confirm.canonical_tags), confirm.is_rental, confirm.ticket_forecast]);

  const updateConfirm = useCallback(async (eventDetailsToSave) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      let patchUrl, params;
      const roomIds = [...(eventDetailsToSave.room_ids || [])];

      if (confirm.type === "Confirm") {
        patchUrl = `/teams/${team.id}/confirms/${confirm.id}`;
        params = {
          confirm: {
            title: eventDetailsToSave.title,
            buyer_id: eventDetailsToSave.buyer,
            promoter_id: eventDetailsToSave.promoter,
            event_template_id: eventDetailsToSave.event_template_id,
            ticket_forecast: eventDetailsToSave.ticket_forecast,
            canonical_tag_ids: (eventDetailsToSave.canonical_tags || []).map(tag => tag.id),
            is_rental: eventDetailsToSave.is_rental,
            age_restriction: eventDetailsToSave.age_restriction,
            venue_ownership_id: eventDetailsToSave.venue_ownership_id,
            venue_id: eventDetailsToSave.venue_id,
            room_ids: eventDetailsToSave.room_ids,
            resale_price_cap_percentage: eventDetailsToSave.resale_price_cap_percentage === "" ? null : parseInt(eventDetailsToSave.resale_price_cap_percentage, 10)
          }
        };
      } else {
        patchUrl = `/teams/${team.id}/hold_groups/${confirm.id}`;
        params = {
          hold: {
            title: eventDetailsToSave.title,
            buyer_id: eventDetailsToSave.buyer,
            promoter_id: eventDetailsToSave.promoter,
            event_template_id: eventDetailsToSave.event_template_id,
            ticket_forecast: eventDetailsToSave.ticket_forecast,
            canonical_tag_ids: (eventDetailsToSave.canonical_tags || []).map(tag => tag.id),
            is_rental: eventDetailsToSave.is_rental,
            age_restriction: eventDetailsToSave.age_restriction,
            venue_ownership_id: eventDetailsToSave.venue_ownership_id,
            venue_id: eventDetailsToSave.venue_id,
            hold_positions_attributes: (eventDetailsToSave.holdGroup?.hold_group_entries || []).map((holdGroupEntry) => ({
              id: holdGroupEntry.hold.hold_position_id || "",
              start_time: offsetDateForServer(holdGroupEntry.hold.start_time, venueTimeZone),
              position: holdGroupEntry.hold.position,
              is_target: holdGroupEntry.hold.is_target,
              venue_ownership_id: eventDetailsToSave.venue_ownership_id,
              room_ids: roomIds,
              _destroy: holdGroupEntry._destroy
            }))
          }
        };
      }

      const response = await axios.patch(patchUrl, params);

      if (onConfirmSaved) {
        onConfirmSaved({
          ...confirm,
          ...response.data,
          age_restriction: response.data.age_restriction || eventDetailsToSave.age_restriction,
          canonical_tags: response.data.canonical_tags || eventDetailsToSave.canonical_tags || []
        });
      }

      if (onFinanceItemsChanged && response.data.finance_items) {
        onFinanceItemsChanged(response.data.finance_items);
      }

      return response.data;
    } catch (error) {
      const message = error.response?.data?.join?.(', ') || 'Something went wrong';
      toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
      throw error;
    }
  }, [csrfToken, team.id, confirm.id, confirm.type, onConfirmSaved, onFinanceItemsChanged]);

  const holdGroupHoldPositionUpdated = useCallback((date, position) => {
    setEventDetails(prevEventDetails => {
      if (!prevEventDetails.holdGroup) return prevEventDetails;

      const holdGroupEntries = [...prevEventDetails.holdGroup.hold_group_entries].map((holdGroupEntry) => {
        const startDate = new Date(offsetDateForBrowser(holdGroupEntry.hold.start_time, venueTimeZone));
        const actionDate = new Date(offsetDateForBrowser(date, venueTimeZone));

        if (startDate.toDateString() === actionDate.toDateString()) {
          const hold = { ...holdGroupEntry.hold, position: position };
          return { ...holdGroupEntry, hold: hold };
        } else {
          return holdGroupEntry;
        }
      });

      const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: holdGroupEntries };
      return { ...prevEventDetails, holdGroup: newHoldGroup };
    });
  }, []);

  const holdPositionTargetToggled = useCallback((date) => {
    setEventDetails(prevEventDetails => {
      if (!prevEventDetails.holdGroup) return prevEventDetails;

      const holdGroupEntries = [...prevEventDetails.holdGroup.hold_group_entries].map((holdGroupEntry) => {
        const startDate = new Date(offsetDateForBrowser(holdGroupEntry.hold.start_time, venueTimeZone));
        const actionDate = new Date(offsetDateForBrowser(date, venueTimeZone));

        if (startDate.toDateString() === actionDate.toDateString()) {
          const hold = { ...holdGroupEntry.hold, is_target: !holdGroupEntry.hold.is_target };
          return { ...holdGroupEntry, hold: hold };
        } else {
          return holdGroupEntry;
        }
      });

      const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: holdGroupEntries };
      return { ...prevEventDetails, holdGroup: newHoldGroup };
    });
  }, []);

  const holdGroupHoldRemoved = useCallback((date) => {
    setEventDetails(prevEventDetails => {
      if (!prevEventDetails.holdGroup) return prevEventDetails;

      const holdGroupEntries = [...prevEventDetails.holdGroup.hold_group_entries].map((holdGroupEntry) => {
        const startDate = new Date(offsetDateForBrowser(holdGroupEntry.hold.start_time, venueTimeZone));
        const actionDate = new Date(offsetDateForBrowser(date, venueTimeZone));

        if (startDate.toDateString() === actionDate.toDateString()) {
          return { ...holdGroupEntry, _destroy: true };
        } else {
          return holdGroupEntry;
        }
      });

      const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: holdGroupEntries };
      return { ...prevEventDetails, holdGroup: newHoldGroup };
    });

    setHoldGroupDates(prevDates =>
      prevDates.filter(d => d.toDateString() !== new Date(offsetDateForBrowser(date, venueTimeZone)).toDateString())
    );
  }, []);

  const holdGroupCalendarDateClicked = useCallback((date, selected) => {
    const actionDate = new Date(offsetDateForBrowser(date, venueTimeZone));

    setEventDetails(prevEventDetails => {
      if (!prevEventDetails.holdGroup) {
        const newHoldGroupEntry = { hold: { start_time: actionDate, position: team.default_top_hold || 1 } };
        const newHoldGroup = { hold_group_entries: [newHoldGroupEntry] };
        return { ...prevEventDetails, holdGroup: newHoldGroup };
      }

      const holdGroupEntries = prevEventDetails.holdGroup.hold_group_entries || [];
      const holdGroupEntry = holdGroupEntries.find((entry) => {
        const startDate = new Date(offsetDateForBrowser(entry.hold.start_time, venueTimeZone));
        return startDate.toDateString() === actionDate.toDateString();
      });

      let newHoldGroupEntries;
      if (holdGroupEntry) {
        newHoldGroupEntries = [...holdGroupEntries].map((entry) => {
          const startDate = new Date(offsetDateForBrowser(entry.hold.start_time, venueTimeZone));
          if (startDate.toDateString() === actionDate.toDateString()) {
            return { ...entry, _destroy: !entry._destroy };
          } else {
            return entry;
          }
        });
      } else {
        const newHoldGroupEntry = { hold: { start_time: actionDate, position: team.default_top_hold || 1 } };
        newHoldGroupEntries = [...holdGroupEntries, newHoldGroupEntry];
      }

      const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: newHoldGroupEntries };
      return { ...prevEventDetails, holdGroup: newHoldGroup };
    });

    if (selected) {
      setHoldGroupDates(prevDates =>
        prevDates.filter(d => d.toDateString() !== actionDate.toDateString())
      );
    } else {
      setHoldGroupDates(prevDates => [...prevDates, actionDate]);

      if (confirm.room_ids && confirm.room_ids.length > 0) {
        const dateKey = `${confirm.room_ids.join(',')}-${actionDate.toISOString().split('T')[0]}`;

        if (!fetchedAvailabilityDates.current.has(dateKey)) {
          fetchedAvailabilityDates.current.add(dateKey);

          (async () => {
            try {
              const dateString = actionDate.toISOString().split('T')[0];
              const response = await axios.get(`/teams/${team.id}/holds/availity`, {
                params: {
                  room_ids: confirm.room_ids,
                  date: dateString,
                  calendar_event_id: confirm.id
                }
              });

              const holdAvailability = response.data;

              setEventDetails(prevEventDetails => {
                if (!prevEventDetails.holdGroup) return prevEventDetails;

                const holdGroupEntries = [...prevEventDetails.holdGroup.hold_group_entries].map((entry) => {
                  const startDate = new Date(offsetDateForBrowser(entry.hold.start_time, venueTimeZone));

                  if (startDate.toDateString() === actionDate.toDateString()) {
                    const defaultTopHold = team.default_top_hold || 1;
                    const firstAvailable = holdAvailability.slice(defaultTopHold - 1, -1).find((availity) => {
                      return availity.hold_title === null;
                    });

                    let hold;
                    if (entry._destroy) {
                      hold = { ...entry.hold };
                    } else {
                      hold = {
                        ...entry.hold,
                        position: getHoldPosition(entry.hold, firstAvailable, defaultTopHold),
                        hold_availability: holdAvailability
                      };
                    }

                    return { ...entry, hold: hold };
                  } else {
                    return entry;
                  }
                });

                const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: holdGroupEntries };
                return { ...prevEventDetails, holdGroup: newHoldGroup };
              });
            } catch (error) {
              console.error('Error fetching hold availability:', error);
              fetchedAvailabilityDates.current.delete(dateKey);
            }
          })();
        }
      }
    }
  }, [team.id, team.default_top_hold, confirm.room_ids]);

  const fetchHoldAvailability = useCallback(async (team, roomIds, date) => {
    try {
      if(roomIds.length === 0) {
        return;
      }

      const dateString = date.toISOString().split('T')[0];
      const response = await axios.get(`/teams/${team.id}/holds/availity`, {
        params: {
          room_ids: roomIds,
          date: dateString,
          calendar_event_id: confirm.id
        }
      });

      const holdAvailability = response.data;
      const actionDate = new Date(offsetDateForBrowser(date, venueTimeZone));

      setEventDetails(prevEventDetails => {
        if (!prevEventDetails.holdGroup) return prevEventDetails;

        const holdGroupEntries = [...prevEventDetails.holdGroup.hold_group_entries].map((holdGroupEntry) => {
          const startDate = new Date(offsetDateForBrowser(holdGroupEntry.hold.start_time, venueTimeZone));

          if (startDate.toDateString() === actionDate.toDateString()) {
            const defaultTopHold = team.default_top_hold || 1;
            const firstAvailable = holdAvailability.slice(defaultTopHold - 1, -1).find((availity) => {
              return availity.hold_title === null;
            });

            let hold;
            if (holdGroupEntry._destroy) {
              hold = { ...holdGroupEntry.hold };
            } else {
              hold = {
                ...holdGroupEntry.hold,
                position: getHoldPosition(holdGroupEntry.hold, firstAvailable, defaultTopHold),
                hold_availability: holdAvailability
              };
            }

            return { ...holdGroupEntry, hold: hold };
          } else {
            return holdGroupEntry;
          }
        });

        const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: holdGroupEntries };
        return { ...prevEventDetails, holdGroup: newHoldGroup };
      });
    } catch (error) {
      console.error('Error fetching hold availability:', error);
    }
  }, [team.id, team.default_top_hold]);

  const debouncedFetchBuyers = _.debounce((inputValue, callback) => {
    fetchBuyers(inputValue).then(options => {
      callback(options);
    }).catch(error => {
      console.error('Error in debouncedFetchBuyers:', error);
      toastError("Could not fetch buyers");
      callback([]);
    });
  }, 250);

  const debouncedFetchPromoters = _.debounce((inputValue, callback) => {
    fetchPromoters(inputValue).then(options => {
      callback(options);
    }).catch(error => {
      console.error('Error in debouncedFetchPromoters:', error);
      toastError("Could not fetch promoters");
      callback([]);
    });
  }, 250);

  const computeVenueInfo = (selectedRooms, venueOwnerships) => {
    const roomIds = selectedRooms.map(room => room.value);
    const venueOwnershipId = selectedRooms[0]?.venueOwnershipId;
    const venueId = (venueOwnerships || [])
      .find(vo => vo.id === venueOwnershipId)
      ?.venueable_id;

    return { roomIds, venueOwnershipId, venueId };
  };

  const isValidConfirm = (
    confirm.type === "Confirm"
  );

  const isValidHold = (
    confirm.type === "Hold"
      && selectedRooms.length > 0
      && eventDetails.holdGroup
      && eventDetails.holdGroup.hold_group_entries
      && eventDetails.holdGroup.hold_group_entries.length > 0
      && eventDetails.holdGroup.hold_group_entries.some(holdGroupEntry => {
        return !holdGroupEntry._destroy;
      })
      && eventDetails.holdGroup.hold_group_entries.every(holdGroupEntry => {
        return holdGroupEntry.hold.position;
      })
  );

  const canSubmitForm = (
    eventDetails.title
      && eventDetails.title.length > 0
      && (isValidConfirm || isValidHold)
  )

  const venueTimeZone = confirm.venue_ownership?.tzinfo_identifier ||
                        confirm.venue?.time_zone ||
                        'America/New_York';

  const holdPositionsInOrder = (confirm.hold_positions || []).sort((a, b) => {
    return new Date(a.start_time) - new Date(b.start_time);
  });

  const copyHoldDates = () => {
    const text = buildHoldDatesText(
      holdPositionsInOrder,
      confirm.venue?.name,
      venueTimeZone,
      team?.append_availability_disclaimer_to_hold_dates,
    );
    copyText(text).then((ok) => {
      if (ok) {
        toastSuccess('Copied hold dates to clipboard');
      } else {
        toastError("Couldn't copy hold dates to clipboard");
      }
    });
  };

  useEffect(() => {
    setBuyers(initialBuyers);
  }, [JSON.stringify(initialBuyers)]);

  useEffect(() => {
    setPromoters(initialPromoters);
  }, [JSON.stringify(initialPromoters)]);

  useEffect(() => {
    if (team && team.id && !isGuest) {
      fetchCanonicalTags(team).then(tags => {
        setCanonicalTags(tags);

        if (confirm.canonical_tags && confirm.canonical_tags.length > 0) {
          const genreTags = confirm.canonical_tags.filter(tag =>
            (tag.category === 'genre') || (tag.category_key === 'genre') || (tag.as_json && tag.as_json.category === 'genre')
          );
          const eventTypeTags = confirm.canonical_tags.filter(tag =>
            (tag.category === 'event_type') || (tag.category_key === 'event_type') || (tag.as_json && tag.as_json.category === 'event_type')
          );
          setSelectedGenreTags(genreTags.map(tag => tag.id));
          setSelectedEventTypeTags(eventTypeTags.map(tag => tag.id));
        }
      });

      if (initialBuyers.length === 0) {
        fetchBuyers('').then(buyerOptions => {
          const filteredBuyers = buyerOptions.filter(option => option.value !== '');

          const currentBuyerId = confirm.buyer?.id || confirm.buyer?.value || confirm.buyer;
          if (currentBuyerId && confirm.buyer?.name) {
            const currentBuyerExists = filteredBuyers.some(b => b.value === currentBuyerId);
            if (!currentBuyerExists) {
              filteredBuyers.unshift({
                value: currentBuyerId,
                label: confirm.buyer.name
              });
            }
          }

          setBuyers(filteredBuyers);
        });
      }

      if (initialPromoters.length === 0) {
        fetchPromoters('').then(promoterOptions => {
          const filteredPromoters = promoterOptions.filter(option => option.value !== '');

          const currentPromoterId = confirm.promoter?.id || confirm.promoter?.value || confirm.promoter;
          if (currentPromoterId && confirm.promoter?.name) {
            const currentPromoterExists = filteredPromoters.some(p => p.value === currentPromoterId);
            if (!currentPromoterExists) {
              filteredPromoters.unshift({
                value: currentPromoterId,
                label: confirm.promoter.name
              });
            }
          }

          setPromoters(filteredPromoters);
        });
      }
    }
  }, [team.id, initialBuyers.length, initialPromoters.length, JSON.stringify(confirm.canonical_tags), isGuest]);

  useEffect(() => {
    const tagsToUse = eventDetails.canonical_tags || confirm.canonical_tags || [];
    if (tagsToUse.length > 0 && canonicalTags.length > 0) {
      const genreTags = tagsToUse.filter(tag =>
        (tag.category === 'genre') || (tag.category_key === 'genre') || (tag.as_json && tag.as_json.category === 'genre')
      );
      const eventTypeTags = tagsToUse.filter(tag =>
        (tag.category === 'event_type') || (tag.category_key === 'event_type') || (tag.as_json && tag.as_json.category === 'event_type')
      );
      const genreTagIds = genreTags.map(tag => tag.id);
      const eventTypeTagIds = eventTypeTags.map(tag => tag.id);

      setSelectedGenreTags(prevGenreTags => {
        if (!_.isEqual(prevGenreTags, genreTagIds)) {
          return genreTagIds;
        }
        return prevGenreTags;
      });

      setSelectedEventTypeTags(prevEventTypeTags => {
        if (!_.isEqual(prevEventTypeTags, eventTypeTagIds)) {
          return eventTypeTagIds;
        }
        return prevEventTypeTags;
      });
    }
  }, [JSON.stringify(eventDetails.canonical_tags), JSON.stringify(confirm.canonical_tags), canonicalTags.length]);


  useEffect(() => {
    if (eventDetails && eventDetails.holdGroup) {
      const dates = eventDetails.holdGroup.hold_group_entries.map((holdGroupEntry) => {
        return new Date(offsetDateForBrowser(holdGroupEntry.hold.start_time, venueTimeZone));
      });
      setHoldGroupDates(dates);

      if (confirm.room_ids && confirm.room_ids.length > 0 && !isGuest) {
        eventDetails.holdGroup.hold_group_entries.forEach(async (holdGroupEntry) => {
          const holdDate = new Date(offsetDateForBrowser(holdGroupEntry.hold.start_time, venueTimeZone));
          const dateKey = `${confirm.room_ids.join(',')}-${holdDate.toISOString().split('T')[0]}`;

          if (!fetchedAvailabilityDates.current.has(dateKey) && !holdGroupEntry.hold.hold_availability) {
            fetchedAvailabilityDates.current.add(dateKey);

            try {
              const dateString = holdDate.toISOString().split('T')[0];
              const response = await axios.get(`/teams/${team.id}/holds/availity`, {
                params: {
                  room_ids: confirm.room_ids,
                  date: dateString,
                  calendar_event_id: confirm.id
                }
              });

              const holdAvailability = response.data;

              setEventDetails(prevEventDetails => {
                if (!prevEventDetails.holdGroup) return prevEventDetails;

                const holdGroupEntries = [...prevEventDetails.holdGroup.hold_group_entries].map((entry) => {
                  const startDate = new Date(offsetDateForBrowser(entry.hold.start_time, venueTimeZone));

                  if (startDate.toDateString() === holdDate.toDateString()) {
                    const defaultTopHold = team.default_top_hold || 1;
                    const firstAvailable = holdAvailability.slice(defaultTopHold - 1, -1).find((availity) => {
                      return availity.hold_title === null;
                    });

                    let hold;
                    if (entry._destroy) {
                      hold = { ...entry.hold };
                    } else {
                      hold = {
                        ...entry.hold,
                        position: getHoldPosition(entry.hold, firstAvailable, defaultTopHold),
                        hold_availability: holdAvailability
                      };
                    }

                    return { ...entry, hold: hold };
                  } else {
                    return entry;
                  }
                });

                const newHoldGroup = { ...prevEventDetails.holdGroup, hold_group_entries: holdGroupEntries };
                return { ...prevEventDetails, holdGroup: newHoldGroup };
              });
            } catch (error) {
              console.error('Error fetching hold availability:', error);
              fetchedAvailabilityDates.current.delete(dateKey);
            }
          }
        });
      }
    } else {
      setHoldGroupDates([]);
      fetchedAvailabilityDates.current.clear();
    }
  }, [eventDetails.holdGroup?.hold_group_entries?.length, confirm.room_ids, team.id, team.default_top_hold]);

  const refreshTagData = async (updateParent = false) => {
    if (team && confirm) {
      const [items, tags] = await Promise.all([
        fetchTaggedItems(csrfToken, team, confirm),
        fetchAvailableTags(csrfToken, team)
      ]);
      setTaggedItems(items);
      setAvailableTags(tags);

      if (updateParent && onConfirmSaved) {
        const updatedTags = items.map(item => item.tag);
        onConfirmSaved({
          ...confirm,
          tags: updatedTags
        });
      }
    }
  };

  useEffect(() => {
    if (team && confirm && confirm.id && !isGuest) {
      fetchTaggedItems(csrfToken, team, confirm).then(items => {
        setTaggedItems(items);
      });
      fetchAvailableTags(csrfToken, team).then(tags => {
        setAvailableTags(tags);
      });
    }
  }, [team?.id, confirm?.id, csrfToken, isGuest]);

  // When rendered with openOnMount (e.g. from the mobile shell), open the
  // edit modal immediately by populating eventDetails. We only fire this on
  // first mount; subsequent open/close is driven by the user via "Edit
  // Details" or the Popup's close handler below.
  const didAutoOpenRef = useRef(false);
  useEffect(() => {
    if (openOnMount && !didAutoOpenRef.current) {
      didAutoOpenRef.current = true;
      fetchEventDetails();
    }
  }, [openOnMount, fetchEventDetails]);

  const handledEditRequestRef = useRef(0);
  useEffect(() => {
    if (editRequest > handledEditRequestRef.current) {
      handledEditRequestRef.current = editRequest;
      fetchEventDetails();
    }
  }, [editRequest, fetchEventDetails]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTagPopup && !event.target.closest('.tag-popup-container')) {
        setShowTagPopup(false);
      }
    };

    if (showTagPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTagPopup]);

  const EditableOption = (props) => {
    const { data, type, options } = props;
    return (
      <components.Option {...props}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>{data.label}</span>
          {options.some((option) => option.value === data.value) && (
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRenameTarget({ type, id: data.value, label: data.label });
                  setRenameName(data.label);
                }}
                style={{
                  color: 'var(--sl-text-white)',
                  background: 'none',
                  border: 'none',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--sl-fg-brand)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--sl-text-white)'}
              >
                <i className="fas fa-pencil-alt"></i>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget({ type, id: data.value, label: data.label });
                }}
                style={{
                  color: 'var(--sl-text-white)',
                  background: 'none',
                  border: 'none',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--sl-text-error)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--sl-text-white)'}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          )}
        </div>
      </components.Option>
    );
  };

  const canonicalTagsToDisplay = (Object.keys(eventDetails).length > 0 && eventDetails.canonical_tags)
    ? eventDetails.canonical_tags
    : (confirm.canonical_tags || []);
  const eventTypeTagsForRow = canonicalTagsToDisplay.filter(tag =>
    (tag.category === 'event_type') || (tag.category_key === 'event_type') || (tag.as_json && tag.as_json.category === 'event_type')
  );

  return (
    <React.Fragment>
      <div className={`${variant === "classic" ? "" : "pt-2 pb-1 "}d-flex flex-wrap align-items-center`} style={{ rowGap: '8px', columnGap: '0' }}>
        {eventTypeTagsForRow.map((tag, index) => (
          <div
            key={`event-type-${tag.id || index}`}
            className="d-inline-flex align-items-center mb-2 mr-2"
            style={{
              backgroundColor: 'var(--sl-bg-primary)',
              color: 'var(--sl-text-brand)',
              border: '1px solid var(--sl-border-brand)',
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px',
              gap: '8px',
              whiteSpace: 'nowrap'
            }}
          >
            {tag.name}
          </div>
        ))}
        {variant !== "classic" ? (<>
        {confirm.type === "Confirm" && confirm.start_time ? (
          <Tippy content="Date and Time">
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <CalendarIcon width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.multi_day ? (confirm.human_date_and_time_with_day_of_week || formatTime(confirm.start_time, venueTimeZone)) : formatTime(confirm.start_time, venueTimeZone)}
            </span>
          </Tippy>
        ) : confirm.type === "Hold" && holdPositionsInOrder.length > 0 ? (
          <span className="mb-2 mr-2 badge badge-confirm-metadata">
            <Tippy content={
              holdPositionsInOrder
                .map((hp, index) =>
                  <div key={index}>
                    {`H${hp.position}`} - {formatDate(hp.start_time)}
                  </div>
                )
            }>
              <span>
                <CalendarIcon width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
                {holdPositionsInOrder.length === 1 ? (
                  <span>
                    <strong className='mr-1'>
                      1 Hold
                    </strong>
                    {formatDate(holdPositionsInOrder[0].start_time, venueTimeZone)}
                  </span>
                ) : (
                  <span>
                    <strong className='mr-1'>
                      {holdPositionsInOrder.length} Holds
                    </strong>
                    {formatDate(holdPositionsInOrder[0].start_time, venueTimeZone)} - {formatDate(holdPositionsInOrder[holdPositionsInOrder.length - 1].start_time, venueTimeZone)}
                  </span>
                )}
              </span>
            </Tippy>
            <Tippy content="Copy hold dates">
              <button
                type="button"
                aria-label="Copy hold dates"
                onClick={copyHoldDates}
                className="ml-2 p-0 border-0 bg-transparent"
                style={{ color: 'inherit', font: 'inherit', lineHeight: 'inherit', cursor: 'pointer' }}
              >
                <Copy01 width="1em" height="1em" aria-hidden="true" focusable="false" style={{ verticalAlign: '-0.125em' }} />
              </button>
            </Tippy>
          </span>
        ) : null}
        {confirm.venue && confirm.venue_ownership_rooms_count === 1 ? (
          <Tippy content={confirm.venue.name}>
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <Building07 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.venue.name}{confirm.rooms?.[0] ? ` - ${confirm.rooms[0].name}` : ''}
            </span>
          </Tippy>
        ) : confirm.venue && confirm.rooms ? (
          (confirm.rooms || []).sort((a, b) => a.name.localeCompare(b.name)).map((room, index) =>
            <Tippy key={index}
                    content={
                      confirm.rooms.length > 1 ? `Room ${index + 1}` : `Room`
                    }>
              <span className="mb-2 mr-2 badge badge-confirm-metadata">
                <Building07 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
                {confirm.venue.name} - {room.name}
              </span>
            </Tippy>
          )
        ) : null}
        {confirm.age_restriction ? (
          <Tippy content="Age Restriction">
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <Passport width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.age_restriction === "Under 18 must be accompanied by parent or guardian" ? "Under 18 w/ Guardian" : confirm.age_restriction}
            </span>
          </Tippy>
        ) : null}
        {confirm.venue?.city_and_state ? (
          <Tippy content="Location">
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <MarkerPin02 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.venue.city_and_state}
            </span>
          </Tippy>
        ) : null}
        {confirm.buyer?.name ? (
          <Tippy content="Buyer">
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <UserSquare width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.buyer.name}
            </span>
          </Tippy>
        ) : null}
        {confirm.promoter?.name ? (
          <Tippy content="Promoter">
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <Announcement01 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.promoter.name}
            </span>
          </Tippy>
        ) : null}
        {confirm.event_template?.name ? (
          <Tippy content="Event Template">
            <span className="mb-2 mr-2 badge badge-confirm-metadata">
              <LayoutGrid01 width={16} height={16} className="badge-confirm-metadata-icon" aria-hidden="true" />
              {confirm.event_template.name}
            </span>
          </Tippy>
        ) : null}
        </>) : null}
        <div className={`${variant === "classic" ? "d-flex" : "d-inline-flex"} align-items-center flex-wrap`} style={{ gap: '12px', marginLeft: variant === "classic" ? 0 : '12px' }}>
        {(() => {
          const genreTags = canonicalTagsToDisplay.filter(tag =>
            (tag.category === 'genre') || (tag.category_key === 'genre') || (tag.as_json && tag.as_json.category === 'genre')
          );

          return (
            <>
              {genreTags.length > 0 && variant === "classic" ? (
                <EventHeaderField icon={MagicWand02} label="Genres">
                  {genreTags.map((tag, index) => (
                    <Tag key={`genre-${tag.id || index}`} size="sm" dot>{tag.name}</Tag>
                  ))}
                </EventHeaderField>
              ) : genreTags.length > 0 && (
                <div className="d-inline-flex align-items-center"
                  style={{
                    backgroundColor: 'var(--sl-bg-primary)',
                    border: '1px solid var(--sl-border-primary)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    gap: '12px',
                    whiteSpace: 'nowrap'
                  }}>
                  <span style={{
                    color: 'var(--sl-text-tertiary)',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Genres
                  </span>
                  <div style={{
                    width: '1px',
                    height: '20px',
                    backgroundColor: 'var(--sl-border-secondary)',
                    alignSelf: 'center'
                  }}></div>
                  <div className="d-inline-flex align-items-center" style={{ gap: '6px' }}>
                    {genreTags.map((tag, index) => (
                      <span
                        key={`genre-${tag.id || index}`}
                        style={{
                          backgroundColor: 'var(--setlist-purple-500)',
                          color: 'var(--sl-text-white)',
                          padding: '2px 10px',
                          fontSize: '12px',
                          fontWeight: '400',
                          borderRadius: '16px',
                          display: 'inline-block'
                        }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {(() => {
          const tagsToDisplay = (Object.keys(eventDetails).length > 0 && eventDetails.tags)
            ? eventDetails.tags
            : (confirm.tags || []);

          const tagPopup = showTagPopup && currentUser.can_manage_tagged_items && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1000,
              marginTop: '8px',
              backgroundColor: 'var(--sl-bg-primary)',
              border: '1px solid var(--sl-border-primary)',
              borderRadius: '8px',
              padding: '16px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.05), 0px 10px 15px -3px rgba(16, 24, 40, 0.1)'
            }}>
              <div style={{ fontSize: '14px', color: 'var(--sl-text-primary)', marginBottom: '12px', fontWeight: '600' }}>
                Manage Tags
              </div>

              <div style={{ marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Search or create tag..."
                  value={tagSearchValue}
                  onChange={(e) => setTagSearchValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    border: '1px solid var(--sl-border-primary)',
                    borderRadius: '6px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--sl-fg-brand)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(25, 130, 196, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--sl-border-primary)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{
                maxHeight: '250px',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                padding: '4px 0'
              }}>
                {availableTags
                  .filter(tag =>
                    tag.name.toLowerCase().includes(tagSearchValue.toLowerCase())
                  )
                  .map(tag => {
                    const taggedItem = taggedItems.find(item => item.tag.id === tag.id);
                    const isActive = !!taggedItem;

                    return (
                      <button
                        type="button"
                        key={tag.id}
                        aria-pressed={isActive}
                        onClick={async () => {
                          if (isActive) {
                            const success = await unassignTag(csrfToken, team, taggedItem.id);
                            if (success) {
                              await refreshTagData(true);
                            }
                          } else {
                            const result = await assignTag(csrfToken, team, confirm, tag.id);
                            if (result) {
                              await refreshTagData(true);
                            }
                          }
                        }}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          borderRadius: '14px',
                          cursor: 'pointer',
                          backgroundColor: isActive ? 'var(--sl-fg-brand)' : 'var(--sl-bg-secondary)',
                          color: isActive ? 'var(--sl-text-white)' : 'var(--sl-text-secondary)',
                          border: `1px solid ${isActive ? 'var(--sl-fg-brand)' : 'var(--sl-border-secondary)'}`,
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap',
                          fontFamily: 'inherit',
                          lineHeight: 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'var(--sl-bg-secondary-hover)';
                            e.currentTarget.style.borderColor = 'var(--sl-border-primary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor = 'var(--sl-bg-secondary)';
                            e.currentTarget.style.borderColor = 'var(--sl-border-secondary)';
                          }
                        }}
                      >
                        {tag.name}
                      </button>
                    );
                  })}

                {tagSearchValue && !availableTags.find(tag =>
                  tag.name.toLowerCase() === tagSearchValue.toLowerCase()
                ) && (
                    <button
                      type="button"
                      onClick={async () => {
                        const result = await createAndAssignTag(csrfToken, team, confirm, tagSearchValue);
                        if (result) {
                          await refreshTagData(true);
                          setTagSearchValue('');
                        }
                      }}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        borderRadius: '14px',
                        cursor: 'pointer',
                        backgroundColor: 'var(--sl-bg-brand-secondary)',
                        color: 'var(--sl-fg-brand)',
                        border: '1px solid var(--sl-fg-brand)',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontFamily: 'inherit',
                        lineHeight: 'inherit'
                      }}
                    >
                      <i className="fa fa-plus" style={{ fontSize: '10px' }}></i>
                      Create "{tagSearchValue}"
                    </button>
                  )}
              </div>
            </div>
          );
          if (variant === "classic") {
            return (
              <div className="d-inline-flex align-items-center flex-wrap position-relative tag-popup-container" style={{ gap: '8px' }}>
                {tagsToDisplay.length > 0 || currentUser.can_manage_tagged_items ? (
                  <EventHeaderField icon={Tag01} label="Tags">
                    {tagsToDisplay.map((tag, index) => {
                      const taggedItem = taggedItems.find(item => item.tag && item.tag.name === tag.name);
                      const creatorName = taggedItem && taggedItem.created_by ? taggedItem.created_by.name : null;
                      const removeTag = async () => {
                        if (await unassignTag(csrfToken, team, taggedItem.id)) {
                          await refreshTagData(true);
                        }
                      };
                      return (
                        <Tippy key={`tag-${tag.id || index}`} content={creatorName ? `Added by ${creatorName}` : 'Tag'}>
                          <span>
                            <Tag size="sm" dot onDismiss={currentUser.can_manage_tagged_items && taggedItem ? removeTag : undefined}>
                              {tag.name}
                            </Tag>
                          </span>
                        </Tippy>
                      );
                    })}
                    {currentUser.can_manage_tagged_items ? (
                      <Button variant="tertiary" size="sm" icon={Plus} onClick={(e) => { e.stopPropagation(); setShowTagPopup(!showTagPopup); }}>
                        Add tag
                      </Button>
                    ) : null}
                  </EventHeaderField>
                ) : null}
                {tagPopup}
              </div>
            );
          }
          return (tagsToDisplay.length > 0 || currentUser.can_manage_tagged_items) && (
            <div className="d-inline-flex align-items-center position-relative tag-popup-container"
              style={{
                backgroundColor: 'var(--sl-bg-primary)',
                border: '1px solid var(--sl-border-primary)',
                borderRadius: '8px',
                padding: '8px 12px',
                gap: '12px',
                whiteSpace: 'nowrap'
              }}>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentUser.can_manage_tagged_items) {
                    setShowTagPopup(!showTagPopup);
                  }
                }}
                style={{
                  color: 'var(--sl-text-tertiary)',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: currentUser.can_manage_tagged_items ? 'pointer' : 'default'
                }}>
                Tags
              </span>
              <div style={{
                width: '1px',
                height: '20px',
                backgroundColor: 'var(--sl-border-secondary)',
                alignSelf: 'center'
              }}></div>
              <div className="d-inline-flex align-items-center flex-wrap" style={{ gap: '6px', minWidth: '60px' }}>
                {tagsToDisplay.length > 0 ? (
                  tagsToDisplay.map((tag, index) => {
                    const taggedItem = taggedItems.find(item => item.tag && item.tag.name === tag.name);
                    const creatorName = taggedItem && taggedItem.created_by ? taggedItem.created_by.name : null;

                    return (
                      <Tippy
                        key={`tag-${tag.id || index}`}
                        content={creatorName ? `Added by ${creatorName}` : 'Tag'}
                      >
                        <span
                          style={{
                            backgroundColor: 'var(--sl-fg-brand)',
                            color: 'var(--sl-text-white)',
                            padding: '2px 10px',
                            fontSize: '12px',
                            fontWeight: '400',
                            borderRadius: '16px',
                            display: 'inline-block'
                          }}
                        >
                          {tag.name}
                        </span>
                      </Tippy>
                    );
                  })
                ) : (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentUser.can_manage_tagged_items) {
                        setShowTagPopup(true);
                      }
                    }}
                    style={{
                      color: 'var(--sl-text-disabled)',
                      fontSize: '12px',
                      fontStyle: 'italic',
                      cursor: currentUser.can_manage_tagged_items ? 'pointer' : 'default'
                    }}
                  >
                    {currentUser.can_manage_tagged_items ? 'Click to add tags' : 'No tags'}
                  </span>
                )}
              </div>
              {tagPopup}
            </div>
          );
        })()}
        </div>
        {currentUser.can_update_calendar_event && confirm.id && variant !== "classic" ? (
          <a className="d-inline-flex align-items-center ml-3"
            style={{
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              color: "var(--sl-text-brand)",
              textDecoration: "none",
              gap: "6px"
            }}
            onClick={(e) => {
              e.preventDefault()
              fetchEventDetails();
            }}>
            <i className="fas fa-pencil" style={{ fontSize: "14px" }}></i>
            Edit Details
          </a>
        ) : null}
      </div>
      <Popup
        open={Object.keys(eventDetails).length > 0}
        contentStyle={confirm.type === "Hold" ? holdModalStyle : modalStyle}
        overlayStyle={{ zIndex: 9999 }}
        closeOnDocumentClick={false}
        className={"popup-modal"}
        onClose={() => { setEventDetails({}); if (onClose) onClose(); }} >
        <div className="popup-modal-container">
          <div className="border-bottom p-4 d-flex align-items-center justify-content-between"
            style={{ "borderColor": "var(--sl-border-primary)" }}>
            <h5 className="m-0">
              <strong>Event Details</strong>
            </h5>
            <a href="#"
              className="mr-1"
              style={{ "color": "var(--sl-text-quaternary)", "fontSize": "20px" }}
              onClick={(e) => {
                e.preventDefault();
                setEventDetails({});
                if (onClose) onClose();
              }}>
              <i className="far fa-times"></i>
            </a>
          </div>
          <div className="p-4">
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-title"
                className="mb-1 text-muted">
                Event Template
                <span className="text-optional pl-2">
                  (optional)
                </span>
              </label>
              <EventTemplateSelect
                teamId={team.id}
                clearable
                id="edit-event-template"
                value={eventDetails.event_template || null}
                onChange={(option) => {
                  setEventDetails({
                    ...eventDetails,
                    event_template: option,
                    event_template_id: option ? option.value : null
                  })
                }}
              />
            </div>
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-title"
                className="mb-1 text-muted">
                Event Name
              </label>
              <input type="text"
                id="edit-event-title"
                name="edit-event-title"
                className="form-control"
                style={{ "fontSize": "14px", "borderWidth": "1px", "borderRadius": "4px" }}
                value={eventDetails.title || ""}
                onChange={(e) => setEventDetails({ ...eventDetails, title: e.target.value })} />
            </div>
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-age-restriction"
                className="mb-1 text-muted">
                Age Restriction
                <span className="text-optional pl-2">
                  (optional)
                </span>
              </label>
              <Select
                id="edit-event-age-restriction"
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : null}
                isClearable={true}
                options={ageRestrictionOptions}
                value={eventDetails.age_restriction ? { value: eventDetails.age_restriction, label: eventDetails.age_restriction } : null}
                onChange={(option) => {
                  setEventDetails({
                    ...eventDetails,
                    age_restriction: option ? option.value : null
                  })
                }}
              />
            </div>
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-rooms"
                className="mb-1 text-muted">
                Venue
              </label>
              <RoomSelector
                rooms={rooms}
                venueOwnerships={venueOwnerships}
                selectedRooms={selectedRooms}
                id="edit-event-rooms"
                menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : null}
                onChange={(selected) => {
                  setSelectedRooms(selected || []);
                }}
              />
            </div>
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-promoter"
                className="mb-1 text-muted">
                Promoter
              </label>
              <AsyncSelect
                placeholder={"Select or create a promoter"}
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : null}
                isClearable={true}
                defaultOptions={promoters}
                id="edit-event-promoter"
                value={promoters.find(promoter => promoter.value === eventDetails.promoter)}
                loadOptions={(inputValue, callback) => debouncedFetchPromoters(inputValue, callback)}
                components={{
                  Option: (optionProps) => (
                    <EditableOption
                      {...optionProps}
                      type="promoter"
                      options={promoters}
                    />
                  ),
                }}
                onChange={
                  (option) => {
                    if (option === null) {
                      setEventDetails({ ...eventDetails, promoter: null });
                    } else {
                      const existingPromoter = promoters.find(promoter => promoter.value === option.value);

                      if (existingPromoter) {
                        setEventDetails({ ...eventDetails, promoter: option.value });
                      } else if (option.name) {
                        createPromoter(option.name).then(
                          (newPromoter) => {
                            var newOption = { label: newPromoter.name, value: newPromoter.id };
                            setPromoters([...promoters, newOption]);
                            setEventDetails({ ...eventDetails, promoter: newOption.value });
                            toast.success(`Promoter "${newPromoter.name}" was created`, {
                              position: toast.POSITION.TOP_CENTER,
                              draggable: false,
                              closeOnClick: false,
                              autoClose: 5000,
                              hideProgressBar: true
                            });
                          }
                        ).catch((error) => {
                            setEventDetails({ ...eventDetails, promoter: null });
                            toast.error(`Could not create promoter "${option.name}"`, {
                              position: toast.POSITION.TOP_CENTER,
                              draggable: false,
                              closeOnClick: false,
                              autoClose: 5000,
                              hideProgressBar: true
                            });
                          });
                      } else {
                        setEventDetails({ ...eventDetails, promoter: option.value });
                      }
                    }
                  }
                }
              />
            </div>
            <div className="mb-4 d-flex flex-column">
              <label htmlFor="edit-event-buyer"
                className="mb-1 text-muted">
                Buyer
              </label>
              <AsyncSelect
                placeholder={"Select or create a buyer"}
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : null}
                isClearable={true}
                defaultOptions={buyers}
                id="edit-event-buyer"
                value={buyers.find((buyer) => buyer.value === eventDetails.buyer)}
                loadOptions={(inputValue, callback) => debouncedFetchBuyers(inputValue, callback)}
                components={{
                  Option: (optionProps) => (
                    <EditableOption
                      {...optionProps}
                      type="buyer"
                      options={buyers}
                    />
                  ),
                }}
                onChange={
                  (option) => {
                    if (option === null) {
                      setEventDetails({ ...eventDetails, buyer: null });
                    } else {
                      const existingBuyer = buyers.find(buyer => buyer.value === option.value);

                      if (existingBuyer) {
                        setEventDetails({ ...eventDetails, buyer: option.value });
                      } else if (option.name) {
                        createBuyer(option.name).then(
                          (newBuyer) => {
                            var newOption = { label: newBuyer.name, value: newBuyer.id };
                            setBuyers([...buyers, newOption]);
                            setEventDetails({ ...eventDetails, buyer: newOption.value });
                            toast.success(`Company "${newBuyer.name}" was created`, {
                              position: toast.POSITION.TOP_CENTER,
                              draggable: false,
                              closeOnClick: false,
                              autoClose: 5000,
                              hideProgressBar: true
                            });
                          }
                        ).catch((error) => {
                            setEventDetails({ ...eventDetails, buyer: null });
                            toast.error(`Could not create company "${option.name}"`, {
                              position: toast.POSITION.TOP_CENTER,
                              draggable: false,
                              closeOnClick: false,
                              autoClose: 5000,
                              hideProgressBar: true
                            });
                          });
                      } else {
                        setEventDetails({ ...eventDetails, buyer: option.value });
                      }
                    }
                  }
                }
              />
            </div>
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-type"
                className="mb-1 text-muted">
                Event Type
                <span className="text-optional pl-2">
                  (optional)
                </span>
              </label>
              <Select
                id="edit-event-type"
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : null}
                isClearable={true}
                options={canonicalTags
                  .filter(tag => tag.category === 'event_type')
                  .map(tag => ({ value: tag.id, label: tag.name }))}
                value={canonicalTags
                  .filter(tag => tag.category === 'event_type' && selectedEventTypeTags.includes(tag.id))
                  .map(tag => ({ value: tag.id, label: tag.name }))[0] || null}
                onChange={(option) => {
                  handleCanonicalTagChange(
                    'event_type',
                    option ? option.value : null,
                    setSelectedEventTypeTags,
                    selectedGenreTags,
                    canonicalTags,
                    setEventDetails
                  );
                }}
              />
            </div>
            <div className="form-group" style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <input type="checkbox"
                id="is-rental"
                checked={eventDetails.is_rental || false}
                onChange={(e) => {
                  setEventDetails({ ...eventDetails, is_rental: e.target.checked });
                }} />
              <label htmlFor="is-rental"
                style={{marginBottom: 0}}
                className="text-muted">
                This is a room rental
              </label>
            </div>
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-genre"
                className="mb-1 text-muted">
                Genre
                <span className="text-optional pl-2">
                  (optional)
                </span>
              </label>
              <Select
                id="edit-event-genre"
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('body') : null}
                isClearable={true}
                options={canonicalTags
                  .filter(tag => tag.category === 'genre')
                  .map(tag => ({ value: tag.id, label: tag.name }))}
                value={canonicalTags
                  .filter(tag => tag.category === 'genre' && selectedGenreTags.includes(tag.id))
                  .map(tag => ({ value: tag.id, label: tag.name }))[0] || null}
                onChange={(option) => {
                  handleCanonicalTagChange(
                    'genre',
                    option ? option.value : null,
                    setSelectedGenreTags,
                    selectedEventTypeTags,
                    canonicalTags,
                    setEventDetails
                  );
                }}
              />
            </div>
            {confirm.type === "Hold" ? (
              <HoldPositionPicker csrfToken={csrfToken}
                team={team}
                venueSelected={confirm.venue}
                holdGroup={eventDetails.holdGroup || {}}
                holdGroupCalendarDateClicked={holdGroupCalendarDateClicked}
                holdGroupDates={holdGroupDates}
                makeUnavailable={makeUnavailable}
                setMakeUnavailable={setMakeUnavailable}
                holdGroupHoldPositionUpdated={holdGroupHoldPositionUpdated}
                holdPositionTargetToggled={holdPositionTargetToggled}
                holdGroupHoldRemoved={holdGroupHoldRemoved}
                fetchHoldAvailability={fetchHoldAvailability}
                selectedRooms={selectedRooms}
                calendarStartDate={
                  holdPositionsInOrder.length > 0 ?
                    offsetDateForBrowser(new Date(holdPositionsInOrder[0].start_time), venueTimeZone) :
                    new Date()
                } />
            ) : null}
            <div className="mb-3 d-flex flex-column">
              <label htmlFor="edit-event-ticket-forecast"
                className="mb-1 text-muted">
                Ticket Forecast
              </label>
              <input type="number"
                id="edit-event-ticket-forecast"
                name="edit-event-ticket-forecast"
                className="form-control"
                style={{ "fontSize": "14px", "borderWidth": "1px", "borderRadius": "4px" }}
                value={eventDetails.ticket_forecast || "0"}
                onChange={(e) => setEventDetails({ ...eventDetails, ticket_forecast: e.target.value })} />
            </div>
            {confirm.type === "Confirm" && eventDetails.can_set_resale_price_cap &&
              <div className="mb-3 d-flex flex-column">
                <label htmlFor="edit-event-resale-price-cap"
                  className="mb-1 text-muted">
                  Resale Price Cap (% of price paid)
                </label>
                <input type="number"
                  id="edit-event-resale-price-cap"
                  name="edit-event-resale-price-cap"
                  className="form-control"
                  style={{ "fontSize": "14px", "borderWidth": "1px", "borderRadius": "4px" }}
                  min="100"
                  step="1"
                  placeholder="Uses venue or team default"
                  value={eventDetails.resale_price_cap_percentage ?? ""}
                  onChange={(e) => setEventDetails({ ...eventDetails, resale_price_cap_percentage: e.target.value })} />
              </div>}
            <div>
              <a
                href="#"
                className={"w-100 btn btn-primary font-weight-bold " + (canSubmitForm ? "" : "disabled")}
                onClick={(e) => {
                  e.preventDefault();

                  if (!canSubmitForm) {
                    return false;
                  }

                  var eventDetailsCopy = { ...eventDetails };

                  if (makeUnavailable && confirm.type === "Hold" && eventDetailsCopy && eventDetailsCopy.holdGroup) {
                    var filteredHoldGroupEntries = eventDetailsCopy.holdGroup.hold_group_entries.filter((holdGroupEntry) => {
                      var firstHoldAvailability = (holdGroupEntry.hold.hold_availability || [])[0];
                      return firstHoldAvailability && !firstHoldAvailability.is_confirm;
                    });

                    var filteredHoldGroup = { ...eventDetailsCopy.holdGroup, hold_group_entries: filteredHoldGroupEntries };
                    eventDetailsCopy = { ...eventDetailsCopy, holdGroup: filteredHoldGroup };
                  }

                  const { roomIds, venueOwnershipId, venueId } = computeVenueInfo(selectedRooms, venueOwnerships);
                  eventDetailsCopy.room_ids = roomIds;
                  eventDetailsCopy.venue_ownership_id = venueOwnershipId;
                  eventDetailsCopy.venue_id = venueId;

                  updateConfirm(eventDetailsCopy).then((savedConfirm) => {
                    setEventDetails({});

                    toast.success("Event Details updated", {
                      position: toast.POSITION.TOP_CENTER,
                      draggable: false,
                      closeOnClick: false,
                      autoClose: 5000,
                      hideProgressBar: true
                    });
                  }).catch((error) => {
                  });
                }}>
                Save
              </a>
            </div>
          </div>
        </div>
      </Popup>
      <Popup
        open={renameTarget !== null}
        contentStyle={{ ...modalStyle, width: '360px' }}
        overlayStyle={{ zIndex: 10000 }}
        closeOnDocumentClick={false}
        className={"popup-modal"}
        onClose={() => { setRenameTarget(null); setRenameName(''); }}
      >
        <div className="popup-modal-container">
          <div className="border-bottom p-4 d-flex align-items-center justify-content-between"
            style={{ "borderColor": "var(--sl-border-primary)" }}>
            <h5 className="m-0">
              <strong>Rename {renameTarget?.type === 'promoter' ? 'Promoter' : 'Buyer'}</strong>
            </h5>
            <a href="#"
              className="mr-1"
              style={{ "color": "var(--sl-text-quaternary)", "fontSize": "20px" }}
              onClick={(e) => {
                e.preventDefault();
                setRenameTarget(null);
                setRenameName('');
              }}>
              <i className="far fa-times"></i>
            </a>
          </div>
          <div className="p-4">
            <div className="mb-3 d-flex flex-column">
              <label className="mb-1 text-muted">Name</label>
              <input
                type="text"
                className="form-control"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="border-top p-3 d-flex justify-content-end" style={{ gap: '8px' }}>
            <a href="#"
              className="btn btn-setlist-secondary"
              onClick={(e) => {
                e.preventDefault();
                setRenameTarget(null);
                setRenameName('');
              }}>
              Cancel
            </a>
            <a href="#"
              className={`btn btn-setlist-primary ${!renameName.trim() ? 'disabled' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                if (!renameName.trim()) return;
                const { type, id, label } = renameTarget;
                const updateFn = type === 'promoter' ? updatePromoter : updateBuyer;
                const setOptions = type === 'promoter' ? setPromoters : setBuyers;
                const optionsList = type === 'promoter' ? promoters : buyers;
                const entityLabel = type === 'promoter' ? 'Promoter' : 'Buyer';

                updateFn(id, renameName.trim()).then((updated) => {
                  setOptions(optionsList.map((opt) =>
                    opt.value === id ? { ...opt, label: updated.name } : opt
                  ));
                  toast.success(`${entityLabel} renamed to "${updated.name}".`);
                  setRenameTarget(null);
                  setRenameName('');
                }).catch(() => {
                  toastError(`Failed to rename ${entityLabel.toLowerCase()}.`);
                });
              }}>
              Save
            </a>
          </div>
        </div>
      </Popup>
      <Popup
        open={deleteTarget !== null}
        contentStyle={{ ...modalStyle, width: '360px' }}
        overlayStyle={{ zIndex: 10000 }}
        closeOnDocumentClick={false}
        className={"popup-modal"}
        onClose={() => setDeleteTarget(null)}
      >
        <div className="popup-modal-container">
          <div className="border-bottom p-4 d-flex align-items-center justify-content-between"
            style={{ "borderColor": "var(--sl-border-primary)" }}>
            <h5 className="m-0">
              <strong>Delete {deleteTarget?.type === 'promoter' ? 'Promoter' : 'Buyer'}</strong>
            </h5>
            <a href="#"
              className="mr-1"
              style={{ "color": "var(--sl-text-quaternary)", "fontSize": "20px" }}
              onClick={(e) => {
                e.preventDefault();
                setDeleteTarget(null);
              }}>
              <i className="far fa-times"></i>
            </a>
          </div>
          <div className="p-4">
            <p className="mb-0">
              Are you sure you want to delete <strong>{deleteTarget?.label}</strong>? This action cannot be undone.
            </p>
          </div>
          <div className="border-top p-3 d-flex justify-content-end" style={{ gap: '8px' }}>
            <a href="#"
              className="btn btn-setlist-secondary"
              onClick={(e) => {
                e.preventDefault();
                setDeleteTarget(null);
              }}>
              Cancel
            </a>
            <a href="#"
              className="btn btn-setlist-primary"
              style={{ backgroundColor: 'var(--sl-bg-error-solid)', borderColor: 'var(--sl-bg-error-solid)' }}
              onClick={(e) => {
                e.preventDefault();
                const { type, id, label } = deleteTarget;
                const deleteFn = type === 'promoter' ? deletePromoter : deleteBuyer;
                const setOptions = type === 'promoter' ? setPromoters : setBuyers;
                const optionsList = type === 'promoter' ? promoters : buyers;
                const entityLabel = type === 'promoter' ? 'Promoter' : 'Buyer';

                deleteFn(id).then(() => {
                  setOptions(optionsList.filter((opt) => opt.value !== id));
                  toast.success(`${entityLabel} "${label}" deleted successfully.`);
                  setDeleteTarget(null);
                }).catch(() => {
                  toastError(`Failed to delete ${entityLabel.toLowerCase()} "${label}".`);
                });
              }}>
              Delete
            </a>
          </div>
        </div>
      </Popup>
    </React.Fragment>
  );
};

EventDetails.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  rooms: PropTypes.array,
  venueOwnerships: PropTypes.array,
  buyers: PropTypes.array,
  promoters: PropTypes.array,
  onConfirmSaved: PropTypes.func,
  onFinanceItemsChanged: PropTypes.func,
  openOnMount: PropTypes.bool,
  editRequest: PropTypes.number,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(["default", "classic"])
};

export default EventDetails;
